import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { DatePickerPtBr } from '../../../shared/components/DatePickerPtBr';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { employeeValeParcelaBaixarSchema } from './schemas';
import type { BaixarParcelaInput } from './api';
import type { EmployeeValeParcelaDto } from './types';

export interface EmployeeValeParcelaBaixarDialogProps {
  open: boolean;
  parcela: EmployeeValeParcelaDto | null;
  /** Quantas parcelas ainda em aberto antes da baixa atual — pra avisar que header transita. */
  parcelasEmAbertoAntes: number;
  useBaixarParcela: () => UseMutationResult<
    EmployeeValeParcelaDto,
    Error,
    { valeCodigo: number; parcelaCodigo: number; input: BaixarParcelaInput }
  >;
  onClose: () => void;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

/**
 * Dialog de baixa de parcela do vale (Fase 2 Pacote A — card [h7TE7xcf]).
 *
 * Pre-popula `valorPago` com o valor original da parcela e `dataPagamento` com
 * hoje. Avisa quando a baixa vai transitar o header pra Pago (última parcela
 * em aberto) — efeito colateral validado no smoke 33/33.
 *
 * NF Colaborador opcional fica fora do MVP (contrato aceita o par, UI futura).
 */
export function EmployeeValeParcelaBaixarDialog({
  open,
  parcela,
  parcelasEmAbertoAntes,
  useBaixarParcela,
  onClose,
}: EmployeeValeParcelaBaixarDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const baixar = useBaixarParcela();
  const [dataPagamento, setDataPagamento] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [valorPago, setValorPago] = useState<string>('');
  const [touched, setTouched] = useState(false);

  // Reset durante a render quando o dialog abre numa parcela diferente —
  // pattern oficial pra derivar state de props sem useEffect:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [parcelaIdTrack, setParcelaIdTrack] = useState<number | null>(null);
  const desiredParcelaId = open && parcela ? parcela.id : null;
  if (desiredParcelaId !== parcelaIdTrack) {
    setParcelaIdTrack(desiredParcelaId);
    if (parcela) {
      setDataPagamento(dayjs().format('YYYY-MM-DD'));
      setValorPago(parcela.valor.toFixed(2));
      setTouched(false);
    }
  }

  const parsedValor = Number.parseFloat(valorPago.replace(',', '.'));
  const formValues = { dataPagamento, valorPago: parsedValor };
  const validation = employeeValeParcelaBaixarSchema.safeParse(formValues);
  const fieldError = (field: 'dataPagamento' | 'valorPago'): string | undefined => {
    if (validation.success) return undefined;
    return validation.error.issues.find((i) => i.path[0] === field)?.message;
  };

  const willClosePagamento = parcelasEmAbertoAntes <= 1; // a parcela atual conta como em aberto

  async function handleSubmit(): Promise<void> {
    setTouched(true);
    if (!parcela || !validation.success) return;
    try {
      await baixar.mutateAsync({
        valeCodigo: parcela.valeCodigo,
        parcelaCodigo: parcela.id,
        input: { dataPagamento, valorPago: parsedValor },
      });
      toast.success(
        willClosePagamento
          ? t('features.colaboradores.vales.baixar.toastFinal')
          : t('features.colaboradores.vales.baixar.toastOk'),
      );
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('features.colaboradores.vales.baixar.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {parcela && (
            <Typography variant="body2" color="text.secondary">
              {t('features.colaboradores.vales.baixar.subtitle', {
                n: parcela.id,
                valor: formatBRL(parcela.valor),
                vencimento: dayjs(parcela.dataVencimento).format('DD/MM/YYYY'),
              })}
            </Typography>
          )}

          <DatePickerPtBr
            label={t('features.colaboradores.vales.baixar.dataPagamento')}
            value={dataPagamento || null}
            onChange={(v) => setDataPagamento(v ?? '')}
            slotProps={{
              textField: {
                fullWidth: true,
                onBlur: () => setTouched(true),
                error: touched && Boolean(fieldError('dataPagamento')),
                helperText:
                  (touched && fieldError('dataPagamento')) ??
                  t('features.colaboradores.vales.baixar.dataPagamentoHelper'),
              },
            }}
          />

          <TextField
            label={t('features.colaboradores.vales.baixar.valorPago')}
            value={valorPago}
            onChange={(e) => setValorPago(e.target.value)}
            onBlur={() => setTouched(true)}
            fullWidth
            type="number"
            inputProps={{ step: '0.01', min: 0, inputMode: 'decimal' }}
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
            error={touched && Boolean(fieldError('valorPago'))}
            helperText={
              (touched && fieldError('valorPago')) ??
              t('features.colaboradores.vales.baixar.valorPagoHelper')
            }
          />

          {willClosePagamento && (
            <Alert severity="info">
              {t('features.colaboradores.vales.baixar.lastParcelaWarn')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={baixar.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={baixar.isPending || !parcela}
        >
          {baixar.isPending ? t('common.saving') : t('features.colaboradores.vales.baixar.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
